import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-3" data-testid="reset-pw-invalid">Invalid Reset Link</h1>
          <p className="text-zinc-400 text-sm mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="request-new-link-btn">Request New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-3" data-testid="reset-pw-success-title">Password Reset!</h1>
          <p className="text-zinc-400 text-sm mb-8">Your password has been updated. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight" data-testid="reset-pw-title">Reset Password</h1>
          <p className="text-zinc-500 text-sm mt-2">Enter your new password</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">New Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm"
                  placeholder="New password" data-testid="reset-pw-input" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Confirm Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6}
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm"
                  placeholder="Confirm password" data-testid="reset-pw-confirm-input" />
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="reset-pw-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="reset-pw-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
